import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import { useText, useLink } from 'common/hooks';
import {
  Nav,
  Image,
  Items,
  Language,
  Bar,
  HomeBar,
  Link,
  StyledLink,
  Button,
  List,
  ListItem,
  LogoWrapper,
  SponsorsWrapper,
} from './elements';
import { DuckyLogo } from 'dc/DuckyLogo';
import LazyImage from 'common/components/LazyImage';
import { getOrderedSponsors } from 'utils/challenge';
import { PlmgButton } from '@ducky/plumage-react';

type Props = {
  readonly className?: string;
};

const deps = {
  page: state.app.page,
  returnUrl: state.account.returnUrl,
  locale: state.appContainer.language.locale,
  path: state.app.path,
  loggedIn: state.appContainer.user.uid,
  user: state.appContainer.user,
  profileClicked: sequences`appContainer.profileClicked`,
  campaign: state.challenge.campaign,
};

const supportLink = {
  en: 'https://www.ducky.eco/en/support/ducky-challenge/faq',
  es: 'https://www.ducky.eco/es/support/ducky-challenge/faq',
  no: 'https://www.ducky.eco/no-no/support/ducky-challenge/ofte-stilte-sporsmal',
  ja: 'https://www.ducky.eco/ja/support/ducky-challenge/faq',
};

export function Menu({
  className,
  path,
  loggedIn,
  user,
  page,
  profileClicked,
  locale,
  campaign,
  returnUrl,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const link = useLink();
  const [orderedSponsors, setOrderedSponsors] = useState([]);
  const profileDisabled = page === 'profileSettings';
  const sponsors = campaign && campaign.sponsors;

  const logoDisabled =
    page === 'joinCampaign' ||
    (page.includes('profile') && returnUrl?.includes('joinCampaign'));

  useEffect(() => {
    setOrderedSponsors(getOrderedSponsors(sponsors));
  }, [sponsors]);

  return (
    <Nav className={className} aria-label={t('menu.ducky')}>
      <List role={'menubar'} aria-label={t('menu.ducky')}>
        <ListItem role={'none'}>
          <LogoWrapper>
            {orderedSponsors?.length > 0 && page === 'root' ? (
              <>
                <PlmgButton
                  href={link('')}
                  iconCenter="home"
                  design="borderless"
                  style={{ margin: 0, padding: 0 }}
                />
                <HomeBar />
                <SponsorsWrapper>
                  {orderedSponsors.map(sponsor => (
                    <LazyImage
                      key={sponsor.id}
                      path={sponsor.image}
                      onLoaded={url => (
                        <StyledLink
                          key={sponsor.id}
                          href={sponsor.url}
                          role="menuitem"
                          aria-label={sponsor.name}
                          target="_blank"
                        >
                          <Image src={url} />
                        </StyledLink>
                      )}
                    />
                  ))}
                </SponsorsWrapper>
              </>
            ) : null}
            {(campaign && !orderedSponsors.length) ||
            orderedSponsors.length < 1 ||
            page !== 'root' ? (
              <DuckyLogo disabled={logoDisabled} />
            ) : null}
          </LogoWrapper>
        </ListItem>
        <Items>
          <ListItem role={'none'}>
            <Language popupPosition={'below-left'} />
          </ListItem>
          <ListItem role={'none'}>
            <Link
              as={'a'}
              href={supportLink[locale]}
              target={'_blank'}
              rel={'noopener noreferrer'}
            >
              {t('menu.help')}
            </Link>
          </ListItem>
          {loggedIn && (
            <>
              <Bar />
              {(user.emailVerified ||
                user.providerData?.[0]?.providerId === 'facebook.com') && (
                <ListItem role={'none'}>
                  <Button
                    id={'nav-profile-btn'}
                    onClick={() => {
                      !profileDisabled &&
                        profileClicked({ url: 'profile', returnUrl: path });
                    }}
                  >
                    <Link>{t('menu.account')}</Link>
                  </Button>{' '}
                </ListItem>
              )}
            </>
          )}
        </Items>
      </List>
    </Nav>
  );
}

export default connect<Props, typeof deps>(deps, Menu);
